const { buildGalleryPropsFromAjaxImages } = require('public/templates/modules/product_media/utils/build-gallery-props-from-ajax-images.js');

const DEFAULT_UNABLE_TO_LOAD_MODEL = 'Error: Unable To Load 3D Model';

/**
 * @param {{
 *   id: string,
 *   type: string,
 *   icon: string|null,
 *   cssClass: string,
 *   main: string,
 *   preview: string,
 *   thumb: string,
 *   modelUrl: string,
 *   active: boolean,
 *   caption: string,
 * }} galleryItem
 * @returns {{
 *   id: string,
 *   type: string,
 *   icon: string|null,
 *   class: string,
 *   main: string,
 *   preview: string,
 *   thumb: string,
 *   model_url: string,
 *   active: boolean,
 *   caption: string,
 * }}
 */
function mapGalleryItemToLegacyImage(galleryItem) {
	return {
		id: galleryItem.id,
		type: galleryItem.type,
		icon: galleryItem.icon,
		class: galleryItem.cssClass,
		main: galleryItem.main,
		preview: galleryItem.preview,
		thumb: galleryItem.thumb,
		model_url: galleryItem.modelUrl,
		active: galleryItem.active,
		caption: galleryItem.caption,
	};
}

/**
 * Maps AJAX variant image payloads to legacy product_images Dust template data.
 *
 * @param {unknown} rawImages
 * @param {{
 *   fallbackImages?: unknown[],
 *   content?: { images?: Record<string, string> },
 *   imageAlt?: string,
 * }} [options]
 * @returns {{
 *   caption: string,
 *   content: { images: Record<string, string> },
 *   hasImages: boolean,
 *   hasSecondaryImages: boolean,
 *   image_alt: string,
 *   images: Array<{
 *     id: string,
 *     type: string,
 *     icon: string|null,
 *     class: string,
 *     main: string,
 *     preview: string,
 *     thumb: string,
 *     model_url: string,
 *     active: boolean,
 *     caption: string,
 *   }>,
 *   imagesScroll: boolean,
 *   initial3dModelUrl: string,
 *   mainImage: {
 *     alt_text: string,
 *     caption: string,
 *     main: { url: string },
 *     preview: { url: string },
 *   }|null,
 *   makeLogo: { main: { url: string } }|null,
 *   modelViewerLabels: { unableToLoadModel: string },
 *   showThumbScroll: boolean,
 * }}
 */
function buildLegacyProductImagesFromAjax(rawImages, options = {}) {
	const contentImages = options.content?.images ?? {};
	const gallery = buildGalleryPropsFromAjaxImages(rawImages, {
		fallbackImages: options.fallbackImages,
	});

	const images = gallery.productImages.map(mapGalleryItemToLegacyImage);
	const imageCount = images.length;

	let mainImage = null;
	if (gallery.mainImage !== null) {
		mainImage = {
			alt_text: options.imageAlt ?? '',
			caption: gallery.mainImage.caption,
			main: { url: gallery.mainImage.mainUrl },
			preview: { url: gallery.mainImage.previewUrl || gallery.mainImage.mainUrl },
		};
	}

	let makeLogo = null;
	if (gallery.makeLogo !== null) {
		makeLogo = { main: { url: gallery.makeLogo.mainUrl } };
	}

	let initial3dModelUrl = '';
	if (mainImage === null) {
		const firstModel = images.find((image) => image.type === '3d_model' && image.model_url !== '');
		if (firstModel !== undefined) {
			initial3dModelUrl = firstModel.model_url;
		}
	}

	return {
		caption: mainImage?.caption ?? '',
		content: { images: contentImages },
		hasImages: imageCount > 0,
		hasSecondaryImages: imageCount > 1,
		image_alt: options.imageAlt ?? '',
		images,
		imagesScroll: imageCount < 5,
		initial3dModelUrl,
		mainImage,
		makeLogo,
		modelViewerLabels: {
			unableToLoadModel: contentImages.model_viewer_unable_to_load_model ?? DEFAULT_UNABLE_TO_LOAD_MODEL,
		},
		showThumbScroll: imageCount > 3,
	};
}

module.exports = {
	buildLegacyProductImagesFromAjax,
};
